// Presentational view for /settings/license (docs/09-frontend-surface.md,
// "4. First-ship information architecture (routes)"; docs/04-api-contract.md,
// "5a. License status surface"; docs/08-on-prem-readiness.md, "XIV — Offline
// signed licensing"). Renders a `LicenseStatus` with no data fetching so the
// layout is driven purely by props (container is LicenseSettings.tsx; Storybook
// consumes this view directly). Quantity usage is warn-and-report, never
// blocking; only the terminated `locked` state actually blocks writes.
import { formatDateTime } from "../lib/format";
import {
  LICENSE_STATE_LABELS,
  LICENSE_USAGE_STATE_LABELS,
  licenseStateTone,
  licenseUsageDimensions,
  licenseUsageMeter,
  licenseUsageStateTone,
  type LicenseEntitlements,
  type LicenseStatus,
  type LicenseUsageDimension,
} from "../lib/license";
import {
  Badge,
  Copyable,
  Grid,
  HelpTip,
  Inline,
  InlineError,
  KeyValue,
  type KeyValueRow,
  Page,
  Progress,
  Section,
  Skeleton,
  Stack,
  Text,
} from "../ui";

const numberFormatter = new Intl.NumberFormat();

function formatCount(value: number): string {
  return numberFormatter.format(value);
}

// Consumed arrives as an exact decimal string of arbitrary precision — voice
// minutes accumulate as sixtieths, so a real value looks like
// "12.016666666666667" and ran the caption off its cell. A meter is read at a
// glance, so it shows at most one decimal place; a non-zero amount that would
// round to nothing renders as "<0.1" rather than a misleading "0".
const METER_FRACTION_DIGITS = 1;

function formatExactQuantity(value: string): string {
  const [whole = "", fraction = ""] = value.split(".", 2);
  const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const kept = fraction.slice(0, METER_FRACTION_DIGITS).replace(/0+$/, "");
  if (kept.length > 0) {
    return `${groupedWhole}.${kept}`;
  }
  // Truncated to nothing, but the raw value had something after the point.
  if (whole === "0" && /[1-9]/.test(fraction)) {
    return `<0.${"0".repeat(METER_FRACTION_DIGITS - 1)}1`;
  }
  return groupedWhole;
}

// "12.4 of 1,000,000" — the dimension's own label already names the unit, so
// repeating it here just made the caption longer than its cell.
function meterCaption(dimension: LicenseUsageDimension): string {
  const consumed = formatExactQuantity(dimension.entry.consumed);
  return `${consumed} of ${formatCount(dimension.entry.entitled)}`;
}

function termRows(status: LicenseStatus): KeyValueRow[] {
  const rows: KeyValueRow[] = [
    { label: "Term starts", value: formatDateTime(status.starts_at) },
    { label: "Expires", value: formatDateTime(status.expires_at) },
    { label: "Termination", value: formatDateTime(status.termination_at) },
  ];
  if (status.grace_ends_at) {
    rows.push({
      label: "Grace ends",
      value: formatDateTime(status.grace_ends_at),
    });
  }
  if (status.days_until_expiry != null) {
    const days = status.days_until_expiry;
    const noun = Math.abs(days) === 1 ? "day" : "days";
    rows.push({
      label: "Days until expiry",
      value: `${String(days)} ${noun}`,
    });
  }
  return rows;
}

// The entitlement echoes that carry no metered usage (docs/08, "XIV"): a null
// entitlement is omitted entirely — never rendered as a dead row.
function entitlementRows(entitlements: LicenseEntitlements): KeyValueRow[] {
  const rows: KeyValueRow[] = [];
  if (entitlements.concurrency != null) {
    rows.push({
      label: "Concurrency ceiling",
      value: formatCount(entitlements.concurrency),
    });
  }
  if (entitlements.environments != null) {
    rows.push({
      label: "Environments",
      value: formatCount(entitlements.environments),
    });
  }
  rows.push({
    label: "Air-gapped",
    value: entitlements.airgap ? "Yes" : "No",
  });
  rows.push({
    label: "Telemetry exempt",
    value: entitlements.telemetry_exempt ? "Yes" : "No",
  });
  if (entitlements.feature_flags.length > 0) {
    rows.push({
      label: "Feature flags",
      value: entitlements.feature_flags.join(", "),
    });
  }
  return rows;
}

export interface LicenseSettingsViewProps {
  errorMessage?: string;
  isPending: boolean;
  status?: LicenseStatus;
}

export function LicenseSettingsView({
  errorMessage,
  isPending,
  status,
}: LicenseSettingsViewProps) {
  if (isPending) {
    return (
      <Page variant="config">
        <Skeleton
          label="Loading license status"
          rows={4}
          shape="detail-field"
        />
      </Page>
    );
  }

  if (errorMessage) {
    return (
      <Page variant="config">
        <InlineError>{errorMessage}</InlineError>
      </Page>
    );
  }

  if (!status) {
    return null;
  }

  const stateBadge = (
    <Badge tone={licenseStateTone(status.state)}>
      {LICENSE_STATE_LABELS[status.state]}
    </Badge>
  );

  // Hosted, no license required: one clean line, no term / usage / entitlement
  // scaffolding (docs/08, "XIV"; UI truthfulness — no dead UI).
  if (status.state === "not_required") {
    return (
      <Page variant="config">
        <Section>
          <Stack gap={3}>
            <Inline align="center" gap={3}>
              {stateBadge}
            </Inline>
            <Text variant="muted">
              No license is required for this installation. Licensing applies to
              self-managed and air-gapped deployments.
            </Text>
          </Stack>
        </Section>
      </Page>
    );
  }

  const statusRows: KeyValueRow[] = [];
  if (status.customer) {
    statusRows.push({ label: "Customer", value: status.customer });
  }
  if (status.plan) {
    statusRows.push({
      label: "Plan",
      value: <Copyable label="Plan ID" value={status.plan} />,
    });
  }
  statusRows.push({
    label: "Access",
    value: status.read_only ? "Read-only" : "Full read/write",
  });

  const dimensions = licenseUsageDimensions(status.usage);
  const entitlements = status.entitlements;
  const echoRows = entitlements ? entitlementRows(entitlements) : [];

  // Identity and term are one uninterrupted list of facts about one license —
  // splitting them under "Status" and "Term" headings spent two heading rows
  // and a section gap to say nothing the labels do not.
  const detailRows = status.present
    ? [...statusRows, ...termRows(status)]
    : statusRows;

  return (
    <Page variant="config">
      <Stack gap={4}>
        <Section>
          <Stack gap={3}>
            <Inline align="center" gap={3}>
              {stateBadge}
              {status.warnings.map((warning) => (
                <Badge key={warning} tone="warning">
                  {warning === "expires_in_15_days"
                    ? "Expires in 15 days"
                    : "Expires in 30 days"}
                </Badge>
              ))}
            </Inline>
            {status.state === "locked" ? (
              <Text variant="muted">
                Read-only until the license is renewed.
              </Text>
            ) : null}
            {status.state === "missing" ? (
              <Text variant="muted">
                No license is loaded for this installation.
              </Text>
            ) : null}
            {detailRows.length > 0 ? <KeyValue rows={detailRows} /> : null}
          </Stack>
        </Section>

        {dimensions.length > 0 ? (
          // No product-docs page owns the license / entitlement model yet, so
          // this tip ships without a `docHref` rather than inventing a URL
          // (UI-LAW §4d). Add the link when that page lands.
          <Section
            help={
              <HelpTip label="About license usage">
                {"Usage is measured across the license term. Overage is " +
                  "reported in the signed usage report for commercial " +
                  "reconciliation, and never blocks work."}
              </HelpTip>
            }
          >
            <Grid columns={2} gap={4}>
              {dimensions.map((dimension) => {
                const meter = licenseUsageMeter(dimension.entry);
                return (
                  <Stack gap={2} key={dimension.id}>
                    <Inline align="baseline" gap={2}>
                      <Text as="span">{dimension.label}</Text>
                      <Text as="span" truncate variant="muted">
                        {meterCaption(dimension)}
                      </Text>
                      {dimension.entry.state === "ok" ? null : (
                        <Badge
                          tone={licenseUsageStateTone(dimension.entry.state)}
                        >
                          {LICENSE_USAGE_STATE_LABELS[dimension.entry.state]}
                        </Badge>
                      )}
                    </Inline>
                    <Progress
                      label={`${dimension.label} usage`}
                      max={meter.max}
                      value={meter.value}
                    />
                  </Stack>
                );
              })}
            </Grid>
          </Section>
        ) : null}

        {echoRows.length > 0 ? (
          <Section>
            <KeyValue rows={echoRows} />
          </Section>
        ) : null}
      </Stack>
    </Page>
  );
}
